import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetProfile,
  useListStudents,
  useListClasses,
  useListCourses,
  useCreateStudent,
  useCreateClass,
  useCreateCourse,
  useUpdateStudent,
  useUpdateClass,
  useUpdateCourse,
  useArchiveStudent,
  useDeleteClass,
  useDeleteCourse,
  useListTeacherAssignments,
  useSetTeacherAssignments,
  useGetSchoolAggregatedAnalytics,
  useGetSchoolOverview,
  customFetch,
} from "@workspace/api-client-react";
import type {
  Student,
  Class,
  Course,
  TeacherAssignment,
  SchoolAggregatedAnalytics,
  SchoolAggregatedAnalyticsRecordsByYearLevelItem,
  SchoolAggregatedAnalyticsTopClassesByRecordsItem,
  SchoolAggregatedAnalyticsTopStudentsByRecordsItem,
  SchoolPerTeacherStat,
  SchoolTeacherDetail,
} from "@workspace/api-client-react";
import {
  Building2, Users, UsersRound, Library, GraduationCap, BarChart3,
  Plus, Pencil, Archive, Trash2, Loader2, Lock, ChevronRight, FileText, AlertTriangle,
  TrendingUp, CheckCircle2, Copy, Check, Palette, Upload,
} from "lucide-react";
import { ImportStudentsDialog } from "@/components/import-students-dialog";
import { ImportClassesCoursesDialog } from "@/components/import-classes-courses-dialog";
import { DataMinimisationTip } from "@/components/DataMinimisationTip";
import { formatStudentName, useDefaultStudentDisplayMode } from "@/lib/studentDisplay";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LineChart, Line } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";

const TYPE_COLORS: Record<string, string> = {
  nccd: "#8b5cf6",
  contact: "#3b82f6",
  behaviour: "#ef4444",
  general: "#9ca3af",
};
const TYPE_LABELS: Record<string, string> = {
  nccd: "NCCD",
  contact: "Contact",
  behaviour: "Behaviour",
  general: "General",
};

// ─── Page ────────────────────────────────────────────────────────────────────

export default function SchoolSetupPage() {
  const { data: profile } = useGetProfile();
  const isHeadTeacher = profile?.schoolRole === "head_teacher";

  if (!isHeadTeacher) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <Card>
          <CardContent className="p-10 text-center">
            <Lock className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900">Head teacher access only</h2>
            <p className="text-sm text-gray-500 mt-1">
              School Setup is available to staff with the head teacher role.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 text-primary">
          <Building2 className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">School Setup</h1>
          <p className="text-sm text-gray-500">
            Manage shared students, classes, courses, teacher assignments, and review school-wide analytics.
          </p>
        </div>
      </div>

      <SchoolCodeCard
        schoolCode={profile?.schoolCode ?? null}
        schoolName={profile?.schoolName ?? null}
      />
      {profile?.schoolCode && <SchoolControlsTile />}

      <Tabs defaultValue="students" className="w-full">
        <TabsList className="grid grid-cols-2 sm:grid-cols-5 gap-1">
          <TabsTrigger value="students" data-testid="tab-students">
            <Users className="h-4 w-4 mr-1.5" /> Students
          </TabsTrigger>
          <TabsTrigger value="classes" data-testid="tab-classes">
            <UsersRound className="h-4 w-4 mr-1.5" /> Classes
          </TabsTrigger>
          <TabsTrigger value="courses" data-testid="tab-courses">
            <Library className="h-4 w-4 mr-1.5" /> Subjects
          </TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">
            <GraduationCap className="h-4 w-4 mr-1.5" /> Assignments
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">
            <BarChart3 className="h-4 w-4 mr-1.5" /> Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="students" className="mt-6"><StudentsTab /></TabsContent>
        <TabsContent value="classes" className="mt-6"><ClassesTab /></TabsContent>
        <TabsContent value="courses" className="mt-6"><CoursesTab /></TabsContent>
        <TabsContent value="assignments" className="mt-6"><AssignmentsTab /></TabsContent>
        <TabsContent value="analytics" className="mt-6"><AnalyticsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ─── School code card ───────────────────────────────────────────────────────
// Surfaces the school's join code so head teachers can share it with new
// staff. New teachers enter this code in their settings to join the school.

function SchoolCodeCard({
  schoolCode,
  schoolName,
}: {
  schoolCode: string | null;
  schoolName: string | null;
}) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  if (!schoolCode) {
    return (
      <Card className="border-amber-200 bg-amber-50/40">
        <CardContent className="p-5 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-gray-900">No school code on your profile</p>
            <p className="text-sm text-gray-600 mt-0.5">
              Add a school code in Settings before inviting other staff.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(schoolCode!);
      setCopied(true);
      toast({ title: "School code copied", description: "Share it with new staff." });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // api-error-helper-exempt: navigator.clipboard failure (browser API), not ApiError.
      toast({
        title: "Copy failed",
        description: "Select the code and copy it manually.",
        variant: "destructive",
      });
    }
  }

  return (
    <Card data-testid="card-school-code">
      <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide text-gray-500">Your school code</p>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <code
              className="font-mono text-2xl font-bold text-gray-900 tracking-wider"
              data-testid="text-school-code"
            >
              {schoolCode}
            </code>
            {schoolName && (
              <span className="text-sm text-gray-500 truncate" data-testid="text-school-name">
                {schoolName}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-2">
            New staff enter this code in <span className="font-medium">Settings → School</span> to
            join your school. Once they save, they'll appear in the Assignments tab.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={handleCopy}
          data-testid="button-copy-school-code"
          className="shrink-0"
        >
          {copied ? (
            <>
              <Check className="h-4 w-4 mr-1.5" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-1.5" /> Copy code
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── School controls tile (Task #44) ─────────────────────────────────────────
// Compact, link-style summary of seats + domain lock state. Detail / editing
// happens on the Settings page; this tile gives head teachers an at-a-glance
// view from inside School Setup, where they spend most of their admin time.

type SchoolAccessSummary = {
  schoolCode: string;
  displayName: string | null;
  seatLimit: number | null;
  seatsUsed: number;
  allowedEmailDomains: string[];
  domainLockEnabled: boolean;
  status: "active" | "revoked" | string;
};

function SchoolControlsTile() {
  const [, setLocation] = useLocation();
  const { data, isLoading } = useQuery<SchoolAccessSummary>({
    queryKey: ["/api/school/access"],
    queryFn: () => customFetch<SchoolAccessSummary>("/api/school/access"),
  });

  if (isLoading || !data) return null;

  const seatPct = data.seatLimit ? Math.min(100, Math.round((data.seatsUsed / data.seatLimit) * 100)) : 0;
  const isFull = data.seatLimit !== null && data.seatsUsed >= data.seatLimit;
  const isNearFull = data.seatLimit !== null && !isFull && data.seatsUsed >= Math.floor(data.seatLimit * 0.8);

  return (
    <Card data-testid="card-school-controls">
      <CardContent className="p-5">
        <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
          {/* Seats */}
          <div className="min-w-0">
            <div className="flex items-baseline justify-between mb-1.5">
              <p className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-1">
                <Users className="h-3 w-3" /> Seats
              </p>
              <span className={`text-sm font-semibold ${isFull ? "text-rose-700" : isNearFull ? "text-amber-700" : "text-gray-700"}`} data-testid="text-seats-used">
                {data.seatsUsed}{data.seatLimit !== null ? ` / ${data.seatLimit}` : ""}
              </span>
            </div>
            {data.seatLimit !== null && (
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${isFull ? "bg-rose-500" : isNearFull ? "bg-amber-500" : "bg-teal-500"}`}
                  style={{ width: `${seatPct}%` }}
                />
              </div>
            )}
          </div>

          {/* Domain lock */}
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1.5">Domain lock</p>
            <div className="flex items-center gap-1.5" data-testid="text-domain-lock">
              {data.domainLockEnabled ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 text-teal-600" />
                  <span className="text-sm text-gray-700">
                    On · {data.allowedEmailDomains.length} domain{data.allowedEmailDomains.length === 1 ? "" : "s"}
                  </span>
                </>
              ) : (
                <>
                  <span className="h-2 w-2 rounded-full bg-gray-300" />
                  <span className="text-sm text-gray-500">Off — anyone with the code can join</span>
                </>
              )}
            </div>
          </div>

          {/* Status + edit link */}
          <div className="min-w-0 flex flex-col items-start sm:items-end gap-2">
            <p className="text-xs uppercase tracking-wide text-gray-500">Status</p>
            <Badge className={data.status === "active"
              ? "bg-green-100 text-green-700 border-0"
              : "bg-rose-100 text-rose-700 border-0"}>
              {data.status === "active" ? "Active" : "Revoked"}
            </Badge>
            <button
              type="button"
              onClick={() => setLocation("/app/settings")}
              className="text-xs text-primary hover:underline mt-1"
              data-testid="link-edit-school-controls"
            >
              Manage in Settings →
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Helpers for "recently joined" detection ────────────────────────────────

const RECENT_JOIN_DAYS = 14;

function joinedInfo(joinedAt: string | undefined): { label: string; isRecent: boolean } | null {
  if (!joinedAt) return null;
  const d = new Date(joinedAt);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const isRecent = diffDays >= 0 && diffDays <= RECENT_JOIN_DAYS;
  const label = `Joined ${d.toLocaleDateString(undefined, { month: "short", year: "numeric" })}`;
  return { label, isRecent };
}

// ─── Students tab ────────────────────────────────────────────────────────────

function StudentsTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: students = [], isLoading } = useListStudents();
  const createStudent = useCreateStudent();
  const updateStudent = useUpdateStudent();
  const archiveStudent = useArchiveStudent();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [preferredName, setPreferredName] = useState("");
  const [studentCode, setStudentCode] = useState("");
  const [yearLevel, setYearLevel] = useState("");
  const [notes, setNotes] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const schoolStudents = (students as Student[]).filter((s) => s.schoolId != null);
  const accountDefault = useDefaultStudentDisplayMode();

  function openCreate() {
    setEditing(null);
    setFirstName(""); setLastName(""); setPreferredName(""); setStudentCode("");
    setYearLevel(""); setNotes("");
    setOpen(true);
  }

  function openEdit(s: Student) {
    setEditing(s);
    setFirstName(s.firstName);
    setLastName(s.lastName ?? "");
    setPreferredName((s as any).preferredName ?? "");
    setStudentCode((s as any).studentCode ?? "");
    setYearLevel(s.yearLevel ?? "");
    setNotes(s.notes ?? "");
    setOpen(true);
  }

  async function handleSave() {
    // Phase 2: surname is optional. Only the first name is required.
    if (!firstName.trim()) return;
    try {
      if (editing) {
        await updateStudent.mutateAsync({
          studentId: editing.id,
          data: {
            firstName: firstName.trim(),
            lastName: lastName.trim() || null,
            preferredName: preferredName.trim() || null,
            studentCode: studentCode.trim() || null,
            yearLevel: yearLevel.trim() || null,
            notes: notes.trim() || null,
          } as any,
        });
        toast({ title: "Student updated" });
      } else {
        await createStudent.mutateAsync({
          data: {
            firstName: firstName.trim(),
            lastName: lastName.trim() || null,
            preferredName: preferredName.trim() || undefined,
            studentCode: studentCode.trim() || undefined,
            yearLevel: yearLevel.trim() || undefined,
            notes: notes.trim() || undefined,
            schoolOwned: true,
          } as any,
        });
        toast({ title: "Student added" });
      }
      qc.invalidateQueries();
      setOpen(false);
      setEditing(null);
    } catch (err) {
      toast({
        title: editing ? "Couldn't update the student" : "Couldn't add the student",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  async function handleArchive(student: Student) {
    if (!confirm(`Archive ${formatStudentName(student as Student, accountDefault)}? They will no longer appear in active student lists, but their historical records and evidence will be kept.`)) return;
    try {
      await archiveStudent.mutateAsync({ studentId: student.id, data: { archived: true } });
      qc.invalidateQueries();
      toast({ title: "Student archived" });
    } catch (err) {
      toast({
        title: "Couldn't archive the student",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  const isPending = createStudent.isPending || updateStudent.isPending;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>School-owned students</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Visible to every teacher at your school. {schoolStudents.length} total.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setImportOpen(true)}
            data-testid="button-import-school-students"
          >
            <Upload className="h-4 w-4 mr-1" /> Import CSV
          </Button>
          <Button onClick={openCreate} data-testid="button-new-student" data-write-action>
            <Plus className="h-4 w-4 mr-1" /> Add student
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto my-8" />
        ) : schoolStudents.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">
            No school-owned students yet. Add one to share with all staff.
          </p>
        ) : (
          <div className="divide-y">
            {schoolStudents.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-3" data-testid={`row-student-${s.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{formatStudentName(s as Student, accountDefault)}</p>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide">School</Badge>
                  </div>
                  {s.yearLevel && <p className="text-xs text-gray-500">Year {s.yearLevel}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)}
                    data-testid={`button-edit-student-${s.id}`}>
                    <Pencil className="h-4 w-4 text-gray-500" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleArchive(s)}
                    data-testid={`button-archive-student-${s.id}`}
                    disabled={archiveStudent.isPending}
                    title="Archive student">
                    <Archive className="h-4 w-4 text-amber-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit school-owned student" : "Add school-owned student"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>First name</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  data-testid="input-student-first-name" />
              </div>
              <div>
                <Label>
                  Last name <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                  data-testid="input-student-last-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>
                  Preferred name <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Input value={preferredName} onChange={(e) => setPreferredName(e.target.value)}
                  placeholder="e.g. Sam, Liz"
                  data-testid="input-student-preferred-name" />
              </div>
              <div>
                <Label>
                  Student code <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Input value={studentCode} onChange={(e) => setStudentCode(e.target.value)}
                  placeholder="e.g. school ID, roll number"
                  data-testid="input-student-code" />
              </div>
            </div>
            <div>
              <Label>Year level (optional)</Label>
              <Input value={yearLevel} onChange={(e) => setYearLevel(e.target.value)}
                data-testid="input-student-year-level" />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Shared notes visible to all teachers at your school (e.g. medical, access, NCCD context)."
                rows={3}
                data-testid="input-student-notes"
              />
              <DataMinimisationTip className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={isPending}
              data-testid="button-save-student" data-write-action>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportStudentsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => { qc.invalidateQueries(); }}
        schoolOwned
      />
    </Card>
  );
}

// ─── Classes tab ─────────────────────────────────────────────────────────────

function ClassesTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: classes = [], isLoading } = useListClasses();
  const { data: courses = [] } = useListCourses();
  const createClass = useCreateClass();
  const updateClass = useUpdateClass();
  const deleteClass = useDeleteClass();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Class | null>(null);
  const [name, setName] = useState("");
  const [yearLevel, setYearLevel] = useState("");
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<number>>(new Set());

  const schoolClasses = (classes as Class[]).filter((c) => c.schoolId != null);
  const schoolCourses = (courses as Course[]).filter((c) => c.schoolId != null);
  const courseMap = new Map(schoolCourses.map((c) => [c.id, c]));

  function openCreate() {
    setEditing(null);
    setName(""); setYearLevel(""); setSelectedCourseIds(new Set());
    setOpen(true);
  }

  function openEdit(c: Class) {
    setEditing(c);
    setName(c.name);
    setYearLevel(c.yearLevel ?? "");
    setSelectedCourseIds(new Set(c.courseIds ?? []));
    setOpen(true);
  }

  function toggleCourse(id: number) {
    setSelectedCourseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave() {
    if (!name.trim()) return;
    try {
      const courseIds = Array.from(selectedCourseIds);
      if (editing) {
        await updateClass.mutateAsync({
          classId: editing.id,
          data: {
            name: name.trim(),
            yearLevel: yearLevel.trim() || null,
            courseIds,
          },
        });
        toast({ title: "Class updated" });
      } else {
        await createClass.mutateAsync({
          data: {
            name: name.trim(),
            yearLevel: yearLevel.trim() || undefined,
            courseIds,
            schoolOwned: true,
          },
        });
        toast({ title: "Class added" });
      }
      qc.invalidateQueries();
      setOpen(false);
      setEditing(null);
    } catch (err) {
      toast({
        title: editing ? "Couldn't update the class" : "Couldn't add the class",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Delete this school-owned class? Teacher assignments to it will be removed.")) return;
    await deleteClass.mutateAsync({ classId: id });
    qc.invalidateQueries();
  }

  const isPending = createClass.isPending || updateClass.isPending;
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>School-owned classes</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Shared classes available to assign to any teacher. {schoolClasses.length} total.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkImportOpen(true)}
            data-testid="button-bulk-import-classes-courses"
            data-write-action
          >
            <Upload className="h-4 w-4 mr-1" /> Bulk import CSV
          </Button>
          <Button onClick={openCreate} data-testid="button-new-class" data-write-action>
            <Plus className="h-4 w-4 mr-1" /> Add class
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto my-8" />
        ) : schoolClasses.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No school-owned classes yet.</p>
        ) : (
          <div className="divide-y">
            {schoolClasses.map((c) => {
              const linkedCourses = (c.courseIds ?? [])
                .map((id) => courseMap.get(id))
                .filter((x): x is NonNullable<typeof x> => !!x);
              return (
              <div key={c.id} className="flex items-center justify-between py-3" data-testid={`row-class-${c.id}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{c.name}</p>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide">School</Badge>
                    {c.hidden && (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-gray-500" title="You have hidden this from your own Classes page. Other staff are unaffected.">
                        Hidden in your view
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {c.yearLevel && <Badge variant="secondary" className="text-xs">Year {c.yearLevel}</Badge>}
                    {linkedCourses.map((course) => (
                      <Badge
                        key={course.id}
                        variant="secondary"
                        className="text-xs"
                        style={{ backgroundColor: `${course.color}20`, color: course.color }}
                        data-testid={`badge-class-course-${c.id}-${course.id}`}
                      >
                        {course.name}
                      </Badge>
                    ))}
                    <span className="text-xs text-gray-400">{c.studentCount ?? 0} students</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)}
                    data-testid={`button-edit-class-${c.id}`}>
                    <Pencil className="h-4 w-4 text-gray-500" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)}
                    data-testid={`button-delete-class-${c.id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit school-owned class" : "Add school-owned class"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Class name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                data-testid="input-class-name" />
            </div>
            <div>
              <Label>Year level (optional)</Label>
              <Input value={yearLevel} onChange={(e) => setYearLevel(e.target.value)}
                data-testid="input-class-year-level" />
            </div>
            <div>
              <Label>Subjects (optional)</Label>
              {schoolCourses.length === 0 ? (
                <p className="text-xs text-gray-400 mt-1 border border-dashed rounded-md p-3 text-center">
                  Create subjects on the Subjects tab first.
                </p>
              ) : (
                <div className="border rounded-md max-h-44 overflow-y-auto divide-y mt-1" data-testid="course-checkbox-list">
                  {schoolCourses.map((course) => {
                    const checked = selectedCourseIds.has(course.id);
                    return (
                      <label
                        key={course.id}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 ${checked ? "bg-blue-50/40" : ""}`}
                        data-testid={`course-toggle-${course.id}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggleCourse(course.id)} />
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: course.color }}
                        />
                        <span className="text-sm">{course.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">Tick all subjects this class is for.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={isPending}
              data-testid="button-save-class" data-write-action>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportClassesCoursesDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onDone={() => qc.invalidateQueries()}
      />
    </Card>
  );
}

// ─── Courses tab ─────────────────────────────────────────────────────────────

const COURSE_PRESET_COLORS = [
  "#1a73e8", "#e53935", "#43a047", "#fb8c00", "#8e24aa",
  "#00897b", "#f4511e", "#6d4c41", "#039be5", "#546e7a",
  "#d81b60", "#00acc1",
];
const DEFAULT_COURSE_COLOR = "#6366f1";

function CoursesTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: courses = [], isLoading } = useListCourses();
  const createCourse = useCreateCourse();
  const updateCourse = useUpdateCourse();
  const deleteCourse = useDeleteCourse();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Course | null>(null);
  const [name, setName] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [color, setColor] = useState(DEFAULT_COURSE_COLOR);

  const schoolCourses = (courses as Course[]).filter((c) => c.schoolId != null);

  function openCreate() {
    setEditing(null);
    setName(""); setSubtitle(""); setColor(DEFAULT_COURSE_COLOR);
    setOpen(true);
  }

  function openEdit(c: Course) {
    setEditing(c);
    setName(c.name);
    setSubtitle(c.subtitle ?? "");
    setColor(c.color || DEFAULT_COURSE_COLOR);
    setOpen(true);
  }

  async function handleSave() {
    if (!name.trim()) return;
    try {
      if (editing) {
        await updateCourse.mutateAsync({
          courseId: editing.id,
          data: {
            name: name.trim(),
            subtitle: subtitle.trim() || null,
            color,
          },
        });
        toast({ title: "Subject updated" });
      } else {
        await createCourse.mutateAsync({
          data: {
            name: name.trim(),
            subtitle: subtitle.trim() || undefined,
            color,
            schoolOwned: true,
          },
        });
        toast({ title: "Subject added" });
      }
      qc.invalidateQueries();
      setOpen(false);
      setEditing(null);
    } catch (err) {
      toast({
        title: editing ? "Couldn't update the subject" : "Couldn't add the subject",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Delete this school-owned course?")) return;
    await deleteCourse.mutateAsync({ courseId: id });
    qc.invalidateQueries();
  }

  const isPending = createCourse.isPending || updateCourse.isPending;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>School-owned courses</CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Subjects/programmes shared across the school. {schoolCourses.length} total.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="button-new-course" data-write-action>
          <Plus className="h-4 w-4 mr-1" /> Add course
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto my-8" />
        ) : schoolCourses.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No school-owned courses yet.</p>
        ) : (
          <div className="divide-y">
            {schoolCourses.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-3" data-testid={`row-course-${c.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: c.color }}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-900 truncate">{c.name}</p>
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wide">School</Badge>
                      {c.hidden && (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-gray-500" title="You have hidden this from your own Courses page. Other staff are unaffected.">
                          Hidden in your view
                        </Badge>
                      )}
                    </div>
                    {c.subtitle && <p className="text-xs text-gray-500 truncate">{c.subtitle}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(c)}
                    data-testid={`button-edit-course-${c.id}`}>
                    <Pencil className="h-4 w-4 text-gray-500" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)}
                    data-testid={`button-delete-course-${c.id}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? "Edit school-owned subject" : "Add school-owned subject"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Subject name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                data-testid="input-course-name" />
            </div>
            <div>
              <Label>Subtitle (optional)</Label>
              <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)}
                data-testid="input-course-subtitle" />
            </div>
            <div className="space-y-2">
              <Label>Colour</Label>
              <div className="flex flex-wrap gap-2">
                {COURSE_PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className="w-7 h-7 rounded-full border-2 transition-all"
                    style={{
                      backgroundColor: c,
                      borderColor: color === c ? "#000" : "transparent",
                      transform: color === c ? "scale(1.2)" : "scale(1)",
                    }}
                    data-testid={`button-course-color-${c}`}
                  />
                ))}
                <div className="flex items-center gap-2 ml-1">
                  <Palette className="h-4 w-4 text-gray-400" />
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-7 h-7 rounded cursor-pointer border border-gray-200"
                    data-testid="input-course-color"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={isPending}
              data-testid="button-save-course" data-write-action>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Teacher assignments tab ─────────────────────────────────────────────────

function AssignmentsTab() {
  const qc = useQueryClient();
  const { data: classes = [] } = useListClasses();
  const { data: assignments = [], isLoading } = useListTeacherAssignments();
  const setAssignments = useSetTeacherAssignments();

  const schoolClasses = (classes as Class[]).filter((c) => c.schoolId != null);
  const teacherAssignments = (assignments as TeacherAssignment[]) ?? [];
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [draftClassIds, setDraftClassIds] = useState<number[]>([]);

  function startEdit(t: TeacherAssignment) {
    setEditingUserId(t.userId);
    setDraftClassIds([...(t.classIds ?? [])]);
  }

  function toggleClass(id: number) {
    setDraftClassIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  }

  async function save() {
    if (!editingUserId) return;
    await setAssignments.mutateAsync({
      data: { userId: editingUserId, classIds: draftClassIds },
    });
    qc.invalidateQueries();
    setEditingUserId(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Teacher class assignments</CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Pick which school-owned classes each teacher is responsible for.
          {schoolClasses.length === 0 && " Add school-owned classes first."}
        </p>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto my-8" />
        ) : teacherAssignments.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">No staff at your school yet.</p>
        ) : (
          <div className="divide-y">
            {teacherAssignments.map((t) => {
              const isEditing = editingUserId === t.userId;
              const assignedNames = schoolClasses
                .filter((c) => (t.classIds ?? []).includes(c.id))
                .map((c) => c.name);
              const joined = joinedInfo(t.joinedAt);
              return (
                <div key={t.userId} className="py-4" data-testid={`row-assignment-${t.userId}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900">{t.name}</p>
                        {t.email && (
                          <p
                            className="text-xs text-gray-500 basis-full"
                            data-testid={`text-email-${t.userId}`}
                          >
                            {t.email}
                          </p>
                        )}
                        {joined?.isRecent && (
                          <Badge
                            variant="secondary"
                            className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                            data-testid={`badge-new-${t.userId}`}
                          >
                            New
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {assignedNames.length === 0
                          ? "No classes assigned"
                          : assignedNames.join(", ")}
                        {joined && (
                          <>
                            <span className="mx-1.5">·</span>
                            <span data-testid={`text-joined-${t.userId}`}>{joined.label}</span>
                          </>
                        )}
                      </p>
                    </div>
                    {!isEditing ? (
                      <Button variant="outline" size="sm" onClick={() => startEdit(t)}
                        data-testid={`button-edit-assignment-${t.userId}`}>
                        Edit
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditingUserId(null)}>
                          Cancel
                        </Button>
                        <Button size="sm" onClick={save} disabled={setAssignments.isPending}
                          data-testid={`button-save-assignment-${t.userId}`}>
                          {setAssignments.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                        </Button>
                      </div>
                    )}
                  </div>
                  {isEditing && (
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg">
                      {schoolClasses.length === 0 && (
                        <p className="text-sm text-gray-500 col-span-2">
                          Add school-owned classes first.
                        </p>
                      )}
                      {schoolClasses.map((c) => (
                        <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={draftClassIds.includes(c.id)}
                            onCheckedChange={() => toggleClass(c.id)}
                            data-testid={`checkbox-class-${t.userId}-${c.id}`}
                          />
                          <span className="text-sm">{c.name}{c.yearLevel ? ` (Yr ${c.yearLevel})` : ""}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Analytics tab ───────────────────────────────────────────────────────────

function AnalyticsTab() {
  const [view, setView] = useState<"aggregated" | "individual">("aggregated");

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={view === "aggregated" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("aggregated")}
          data-testid="button-view-aggregated"
        >
          Aggregated (school-wide)
        </Button>
        <Button
          variant={view === "individual" ? "default" : "outline"}
          size="sm"
          onClick={() => setView("individual")}
          data-testid="button-view-individual"
        >
          Individual teacher
        </Button>
      </div>
      {view === "aggregated" ? <AggregatedAnalytics /> : <IndividualTeacherAnalytics />}
    </div>
  );
}

function AggregatedAnalytics() {
  const { data, isLoading } = useGetSchoolAggregatedAnalytics();
  if (isLoading || !data) {
    return <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto my-8" />;
  }
  const a = data as SchoolAggregatedAnalytics;
  const typeData = Object.entries(a.recordsByType ?? {}).map(([type, count]) => ({
    type, label: TYPE_LABELS[type] ?? type, count: count as number, color: TYPE_COLORS[type] ?? "#9ca3af",
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={FileText} label="Total records" value={a.totalRecords} />
        <Stat icon={AlertTriangle} label="Open follow-ups" value={a.openFollowUps} />
        <Stat icon={TrendingUp} label="This week" value={a.recordsThisWeek} />
        <Stat icon={CheckCircle2} label="In Sentral" value={`${a.enteredCount} / ${a.totalRecords}`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Records by type</CardTitle></CardHeader>
          <CardContent>
            {typeData.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No data yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={typeData}>
                  <XAxis dataKey="label" fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="count">
                    {typeData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Activity (last 7 days)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={a.weeklyActivity ?? []}>
                <XAxis dataKey="date" fontSize={11} />
                <YAxis allowDecimals={false} fontSize={12} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Records by year level</CardTitle></CardHeader>
          <CardContent>
            {(a.recordsByYearLevel ?? []).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No data yet.</p>
            ) : (
              <div className="space-y-2">
                {a.recordsByYearLevel.map((r: SchoolAggregatedAnalyticsRecordsByYearLevelItem) => (
                  <div key={r.yearLevel} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">Year {r.yearLevel}</span>
                    <Badge variant="secondary">{r.count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Top classes</CardTitle></CardHeader>
          <CardContent>
            {(a.topClassesByRecords ?? []).length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">No data yet.</p>
            ) : (
              <div className="space-y-2">
                {a.topClassesByRecords.map((c: SchoolAggregatedAnalyticsTopClassesByRecordsItem) => (
                  <div key={c.classId ?? c.className} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700">{c.className}</span>
                    <Badge variant="secondary">{c.count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Top students by record count</CardTitle></CardHeader>
        <CardContent>
          {(a.topStudentsByRecords ?? []).length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">No data yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {a.topStudentsByRecords.map((s: SchoolAggregatedAnalyticsTopStudentsByRecordsItem) => (
                <div key={s.studentId} className="flex items-center justify-between text-sm py-1.5 px-2 bg-gray-50 rounded">
                  <span className="text-gray-700">{s.studentName}</span>
                  <Badge variant="secondary">{s.count}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function IndividualTeacherAnalytics() {
  const { data: overview, isLoading } = useGetSchoolOverview();
  const teachers: SchoolPerTeacherStat[] = overview?.perTeacher ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: teacherDetail } = useQuery<SchoolTeacherDetail>({
    queryKey: ["school-teacher-detail", selectedId],
    enabled: !!selectedId,
    queryFn: () => customFetch<SchoolTeacherDetail>(`/api/school/teacher/${selectedId}`, { credentials: "include" }),
  });

  if (isLoading) {
    return <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto my-8" />;
  }
  if (teachers.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-gray-500">
          No teachers in your school yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-1">
        <CardHeader><CardTitle className="text-base">Teachers</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {teachers.map((t) => (
              <button
                key={t.userId}
                onClick={() => setSelectedId(t.userId)}
                className={`w-full text-left p-3 hover:bg-gray-50 flex items-center justify-between ${selectedId === t.userId ? "bg-primary/5" : ""}`}
                data-testid={`button-teacher-${t.userId}`}
              >
                <div>
                  <p className="font-medium text-gray-900 text-sm">{t.name}</p>
                  <p className="text-xs text-gray-500">{t.totalRecords} records · {t.recordsThisWeek} this week</p>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-base">
            {teacherDetail ? `${teacherDetail.teacher?.name ?? "Teacher"} — details` : "Select a teacher"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!teacherDetail ? (
            <p className="text-center text-sm text-gray-500 py-8">Pick a teacher to see their breakdown.</p>
          ) : (
            <TeacherDetailPanel detail={teacherDetail} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TeacherDetailPanel({ detail }: { detail: SchoolTeacherDetail }) {
  const typeData = Object.entries(detail.recordsByType ?? {}).map(([type, count]) => ({
    type, label: TYPE_LABELS[type] ?? type, count: count as number, color: TYPE_COLORS[type] ?? "#9ca3af",
  }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={FileText} label="Total" value={detail.totalRecords ?? 0} />
        <Stat icon={AlertTriangle} label="Open follow-ups" value={detail.openFollowUps ?? 0} />
        <Stat icon={TrendingUp} label="This week" value={detail.recentRecords?.length ?? 0} />
        <Stat icon={CheckCircle2} label="In Sentral" value={`${detail.enteredCount ?? 0} / ${detail.totalRecords ?? 0}`} />
      </div>
      {typeData.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">By type</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={typeData}>
              <XAxis dataKey="label" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Bar dataKey="count">
                {typeData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number | string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">{label}</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{value}</p>
          </div>
          <div className="p-2 rounded-lg bg-gray-50 text-primary">
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
